import React, {
  useCallback,
  useEffect,
  useState
} from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import StepModel from '../../../../models/StepModel';
import stepIcon from '../../../../../assets/images/chatbot_ai/step_icon.svg';
import NoActivityPlaceholder from './NoActivityPlaceholder';
import { getStatusMeta } from './stepStatusHelpers';
import StepDetail from './StepDetail';
import { formatSummaryDateWithSeconds } from '../../../../../util/dateUtils';
import ButtonComponent from '../../../../components/ButtonMC';
import LoadingMC from '../../../../components/LoadingMC';

const ITEMS_PER_PAGE = 4;

const getStepTitle = (step) => step?.intention_name;
const getStepDescription = (step) => step?.step_description || step?.description;

const Steps = ({ refreshKey, customerId }) => {
  const { t } = useTranslation();
  const [steps, setSteps] = useState([]);
  const [selectedStepId, setSelectedStepId] = useState(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const { customer } = useSelector((reduxState) => reduxState.mainReducer);

  const customerIdToUse = customerId !== undefined ? customerId : customer?.id;

  const hasNextPage = steps.length < total;

  useEffect(() => {
    if (!customerIdToUse) {
      return;
    }

    setLoading(true);
    const params = {
      customer_id: String(customerIdToUse),
      limit: ITEMS_PER_PAGE,
      skip: 0
    };

    StepModel.get(params)
      .then((response) => {
        const stepsData = response?.data || [];
        const totalCount = response?.total || 0;

        setSteps(stepsData);
        setTotal(totalCount);
      })
      .catch((error) => {
        console.error('Error fetching step steps', error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [customerId, customer, refreshKey, customerIdToUse]);

  const loadMoreSteps = useCallback(() => {
    if (!customerIdToUse || loading || !hasNextPage) {
      return;
    }

    setLoading(true);
    const params = {
      customer_id: String(customerIdToUse),
      limit: ITEMS_PER_PAGE,
      skip: steps.length
    };

    StepModel.get(params)
      .then((response) => {
        const stepsData = response?.data || [];
        const totalCount = response?.total ?? total;

        setSteps((prev) => [...prev, ...stepsData]);
        setTotal(totalCount);
      })
      .catch((error) => {
        console.error('Error fetching step steps', error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [customerIdToUse, hasNextPage, loading, steps.length, total]);

  const handleOpenDetail = (stepId) => {
    setSelectedStepId(stepId);
  };

  const renderDescription = (description, stepId) => {
    if (!description) return null;
    const trimmed = description.slice(0, 50);
    const needsTruncate = description.length > 50;

    return (
      <span className="fz-11" style={{ color: '#45535F' }}>
        {needsTruncate ? `${trimmed}...` : description}
        {needsTruncate && stepId && (
          <button
            type="button"
            className="fz-11 p-0 cursor-pointer"
            style={{
              color: '#198FCC',
              background: 'none',
              border: 'none',
              cursor: 'pointer'
            }}
            onClick={() => handleOpenDetail(stepId)}
          >
            ...ver más
          </button>
        )}
      </span>
    );
  };

  if (!loading && total === 0) {
    return <NoActivityPlaceholder />;
  }

  return (
    <>
      <div className="tw-flex tw-flex-col tw-gap-2 tw-mt-4">
        <LoadingMC loading={loading && steps.length === 0} />
        <ul className="p-0 tw-m-0 tw-flex-shrink-0">
          {steps.map((step, index) => {
            const statusMeta = getStatusMeta(step.step_status);
            const statusLabel = statusMeta?.label;
            const statusActive = statusMeta?.active;

            return (
              <li className="d-flex pb-24 w-100" key={step.id || index} style={{ minWidth: 0 }}>
                <div className="d-flex justify-content-center position-relative w-32">
                  <div className="d-flex h-32 justify-content-center w-32" style={{ borderRadius: '50%' }}>
                    <img src={stepIcon} alt="Flujo" />
                  </div>
                  {index !== steps.length - 1 && (
                    <div className="h-perc-99 mt-32 position-absolute w-2" style={{ backgroundColor: 'lightblue' }} />
                  )}
                </div>

                <div className="d-flex flex flex-column pl-8" style={{ flex: 1, minWidth: 0 }}>
                  <span className="fz-12 mb-4" style={{ color: '#45535F' }}>{getStepTitle(step)}</span>
                  {renderDescription(getStepDescription(step), step.id)}
                  <span className="fz-11 py-4" style={{ color: '#6b7280' }}>
                    {formatSummaryDateWithSeconds(step.created_at || step.updated_at)}
                  </span>
                  {statusLabel && (
                    <span
                      className="d-flex justify-content-center fz-12 px-8 py-2"
                      style={{
                        width: 'fit-content',
                        borderRadius: '5px',
                        background: statusActive ? 'var(--Green-50, #EFFAF3)' : 'var(--Yellow-50, #FFFCEB)',
                        color: statusActive ? 'var(--Green-500, #2E9B62)' : 'var(--Yellow-700, #B65A07)'
                      }}
                    >
                      {statusLabel}
                    </span>
                  )}
                </div>
              </li>
            );
          })}
        </ul>

        {hasNextPage && steps.length > 0 && (
          <div className="tw-flex tw-justify-center tw-mt-4 tw-mb-2 tw-flex-shrink-0">
            <ButtonComponent
              outlined
              size="xs"
              variant="SECONDARY"
              className="tw-border-0 tw-bg-transparent hover:tw-bg-transparent tw-flex tw-items-center tw-justify-center tw-flex-shrink-0 tw-px-2 tw-py-1"
              iconClassName="!tw-w-4 !tw-h-4 tw-flex-shrink-0 tw-text-blue-500"
              text={loading ? t('InboxMC:event.loading') : t('InboxMC:event.showMore')}
              icon="arrow-ios-downward-outline"
              onClick={loadMoreSteps}
              disabled={loading}
            />
          </div>
        )}
      </div>

      {selectedStepId && (
        <StepDetail
          stepId={selectedStepId}
          onClose={() => setSelectedStepId(null)}
        />
      )}
    </>
  );
};

export default Steps;
